import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  isReusablePaymentMethod,
  isReusablePaymentMethodType,
  MarketplacePaymentInformationState,
  PaymentInformationService,
  PaymentMethodInfo,
  PaymentMethodType,
  ResetPaymentMethods,
  SetPaymentMethods,
  WalletPaymentMethodInfo,
  WalletPaymentMethodType
} from '@platform-frontend/marketplace';
import { RefreshWindow } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FetchFlowData, FlowCanceled, PublishFetchedFlowData, SetFlow } from '../flow/flow.actions';
import { PaymentWalletState } from '../payment-wallet';
import { ProductState } from '../product';
import { CheckoutPaymentInformationStateModel } from './model/checkout-payment-information-state-model';

const DEFAULT_STATE: CheckoutPaymentInformationStateModel = {
  loadedPaymentMethods: []
};

@State<CheckoutPaymentInformationStateModel>({
  name: 'checkoutPaymentInformation',
  defaults: DEFAULT_STATE
})
@Injectable()
export class CheckoutPaymentInformationState {
  constructor(private paymentInformationService: PaymentInformationService) {}

  @Selector([MarketplacePaymentInformationState.paymentMethods, PaymentWalletState.enabledWallets])
  static paymentMethods(
    state: CheckoutPaymentInformationStateModel,
    paymentMethods: PaymentMethodInfo[],
    enabledWallets: WalletPaymentMethodType[]
  ): PaymentMethodInfo[] {
    return paymentMethods.filter(m => isReusablePaymentMethodType(m.type) || enabledWallets.includes(m.type));
  }

  @Selector([CheckoutPaymentInformationState.paymentMethods])
  static walletPaymentMethods(state: CheckoutPaymentInformationStateModel, paymentMethods: PaymentMethodInfo[]): WalletPaymentMethodInfo[] {
    const isWalletPaymentInfo = (method: PaymentMethodInfo): method is WalletPaymentMethodInfo => !isReusablePaymentMethod(method);
    return paymentMethods.filter(isWalletPaymentInfo);
  }

  @Selector([ProductState.allowedPaymentMethods, CheckoutPaymentInformationState.walletPaymentMethods])
  static addablePaymentMethodTypes(
    state: CheckoutPaymentInformationStateModel,
    allowedPaymentMethods: PaymentMethodType[],
    walletPaymentMethods: WalletPaymentMethodInfo[]
  ): PaymentMethodType[] {
    return allowedPaymentMethods.filter(
      allowedPaymentMethod => !walletPaymentMethods.some(walletPaymentMethod => walletPaymentMethod.type === allowedPaymentMethod)
    );
  }

  @Selector([CheckoutPaymentInformationState.addablePaymentMethodTypes])
  static canPaymentMethodsBeCreated(state: CheckoutPaymentInformationStateModel, addablePaymentMethodTypes: PaymentMethodType[]): boolean {
    return addablePaymentMethodTypes.length > 0;
  }

  @Action(FetchFlowData)
  loadPaymentMethods(ctx: StateContext<CheckoutPaymentInformationStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT' && flow.type !== 'SUBSCRIPTION_MANAGEMENT') {
      return of(null);
    }

    return this.paymentInformationService.fetchPaymentMethods().pipe(tap(loadedPaymentMethods => ctx.patchState({ loadedPaymentMethods })));
  }

  @Action(PublishFetchedFlowData)
  publishPaymentMethods(ctx: StateContext<CheckoutPaymentInformationStateModel>): Observable<unknown> {
    const { loadedPaymentMethods } = ctx.getState();
    ctx.setState(DEFAULT_STATE);
    return ctx.dispatch(new SetPaymentMethods(loadedPaymentMethods));
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  flowCanceled(ctx: StateContext<CheckoutPaymentInformationStateModel>): Observable<unknown> {
    ctx.setState(DEFAULT_STATE);
    return ctx.dispatch(new ResetPaymentMethods());
  }
}
